import type { Dayjs, ManipulateType } from 'dayjs';
import type { Duration } from 'dayjs/plugin/duration';
import type { WeekDay, TimeSlotOptions, DateValidationResult } from '@callmart/types';
import { DATE_FORMATS } from '@callmart/types';
import { appTimezone } from '../config';
import dayjs, { formatSlotTime } from '@utils/dayjs';

/**
 * Date range with start and end as dayjs objects
 */
export interface DateRange {
    dateFrom: Dayjs;
    dateTo: Dayjs;
}

export const addPadding = (value: number | string): string => {
    return value.toString().padStart(2, '0');
};

export const getCreatedAtForHistoryPopover = (createdAtString: string | Date): string => {
    return formatSlotTime(createdAtString, DATE_FORMATS.DATE_TIME_SHORT);
};

export const getFirstDayOfMonth = (date: string | Date | Dayjs): string => {
    return dayjs(date).startOf('month').format(DATE_FORMATS.ISO_DATETIME);
};

export const getLastDayOfMonth = (date: string | Date | Dayjs): string => {
    return dayjs(date).endOf('month').format(DATE_FORMATS.ISO_DATETIME);
};

export const getNextSixMonths = (): Dayjs[] => {
    const nextSixMonths: Dayjs[] = [];
    for (let i = 1; i <= 6; i++) {
        nextSixMonths.push(dayjs().add(i, 'month'));
    }

    return nextSixMonths;
};

export const setUploadedMonthAndYearToDate = (date: Dayjs, monthForUploading: string): Dayjs => {
    const year = parseInt(monthForUploading.split('-')[0]);
    const month = parseInt(monthForUploading.split('-')[1]);

    return date.year(year).month(month - 1);
};

export const isEndAfterStart = (start: string | Date | Dayjs, end: string | Date | Dayjs): boolean | string => {
    return dayjs(end).isSameOrAfter(dayjs(start)) || 'Das Ende muss nach dem Startzeitpunkt liegen';
};

export const getDateRangeFromStartDateByAddingMonths = (
    startDate: string | Date | Dayjs,
    numOfMonths: number
): DateRange => {
    // Use UTC dates for capacity reports - we want exact calendar months without timezone offset
    const dateFrom = dayjs.utc(startDate).startOf('month');
    const dateTo = dayjs
        .utc(dateFrom)
        .add(numOfMonths - 1, 'months')
        .endOf('month');

    return { dateFrom, dateTo };
};

/** CALL-781: "now" in the app's configured timezone. Bare `dayjs()` is BROWSER-local despite
 *  dayjs.tz.setDefault, so the zone has to be passed explicitly on every call. */
const nowInAppTimezone = () => dayjs().tz(appTimezone);

/** The current calendar month, as a first-of-month key ('YYYY-MM-01'). */
export const currentAppMonth = (): string => nowInAppTimezone().startOf('month').format('YYYY-MM-DD');

/** The current calendar year. MUST come from the same clock as currentAppMonth: the budget month window
 *  is Jan-aligned on this year while the "hide past months" cutoff is that month, so taking one from
 *  browser-local time and the other from the app timezone made them disagree for a few hours each New
 *  Year — a New York user at 2026-12-31 18:30 (already 2027 in Berlin) got the Jan–Dec 2026 window with a
 *  Jan 2027 cutoff, which hid every month column. */
export const currentAppYear = (): number => nowInAppTimezone().year();

export const getUpcomingMonths = (firstMonth: string | Date | Dayjs, numOfMonths: number): Dayjs[] => {
    const months: Dayjs[] = [];
    for (let i = 0; i < numOfMonths; i++) {
        months.push(dayjs(firstMonth, DATE_FORMATS.ISO_DATE).add(i, 'months'));
    }

    return months;
};

export const isStringInYearMonthFormat = (value: string): boolean => {
    const regex = /^\d{4}-\d{2}$/;
    return regex.test(value);
};

export const getDuration = (start: string | Date | Dayjs, end: string | Date | Dayjs): Duration =>
    dayjs.duration(dayjs(end).diff(dayjs(start)));

export const formatLastUploadDate = (date?: string | Date | null): string | null | undefined => {
    if (date) {
        return `${formatSlotTime(date, DATE_FORMATS.DATETIME_DASHED)} Uhr`;
    }

    return date;
};

export const getAllDatesInAMonth = (date: string | Date | Dayjs): Dayjs[] =>
    new Array(dayjs(date).daysInMonth()).fill(null).map((x, i) => dayjs(date).startOf('month').add(i, 'days'));

export const getAllHourSlotsInATimeRange = ({
    startTime,
    endTime,
    displayHoursInHH = false,
    displayMinutesInmm = false,
}: TimeSlotOptions): string[] => {
    let allHourSlots: string[] = [];
    for (let i = startTime; i <= endTime; i++) {
        const hour = displayHoursInHH && i < 10 ? `0${i}` : i;
        allHourSlots = [...allHourSlots, `${hour}:${displayMinutesInmm ? '00' : '0'}`, `${hour}:30`];
    }

    return allHourSlots;
};

export const getAllTimeSlotsInADay = (): string[] => {
    return getAllHourSlotsInATimeRange({ startTime: 0, endTime: 23, displayHoursInHH: true, displayMinutesInmm: true });
};

export const getShortDayNameFromDate = (date: string | Date | Dayjs, locale?: string): string =>
    dayjs(date, DATE_FORMATS.ISO_DATE)
        .locale(locale || 'en')
        .format(DATE_FORMATS.WEEKDAY_SHORT);

export const getStartAndEndOfAMonth = (date: string | Date | Dayjs): DateRange => {
    // Extract year/month and interpret in app timezone (Europe/Berlin)
    // This ensures consistent boundaries regardless of user's local timezone
    const d = dayjs(date);
    const dateStr = `${d.year()}-${String(d.month() + 1).padStart(2, '0')}-01`;
    const monthInBerlin = dayjs.tz(dateStr, appTimezone);
    const dateFrom = monthInBerlin.startOf('month');
    const dateTo = monthInBerlin.endOf('month');
    return { dateFrom, dateTo };
};

export const weekDays: WeekDay[] = [
    { name: 'Montag', value: 1 },
    { name: 'Dienstag', value: 2 },
    { name: 'Mittwoch', value: 3 },
    { name: 'Donnerstag', value: 4 },
    { name: 'Freitag', value: 5 },
    { name: 'Samstag', value: 6 },
    { name: 'Sonntag', value: 7 },
];

export const validateAndFormatStartAndEndDates = (
    startDate: string | Date | Dayjs,
    endDate: string | Date | Dayjs,
    dateRangeType: ManipulateType
): DateValidationResult => {
    const startDayjs = dayjs(startDate).startOf(dateRangeType);
    const endDayjs = dayjs(endDate).endOf(dateRangeType);
    const isValidStart = startDayjs.isValid();
    const isValidEnd = endDayjs.isValid();

    const start = isValidStart ? startDayjs.format(`${DATE_FORMATS.ISO_DATE} 00:00:00`) : null;
    const end = isValidEnd ? endDayjs.format(`${DATE_FORMATS.ISO_DATE} 23:59:59`) : null;

    return { start, end, isValidStart, isValidEnd };
};

/**
 * Extract date-time portion from a slotId.
 * SlotId format: "DD.MM.YYYY-HH:MM:SS-worktypeId" (e.g., "04.12.2025-15:00:00-123")
 * Returns: "DD.MM.YYYY-HH:MM" (e.g., "04.12.2025-15:00")
 */
export const getSlotDateTime = (slotId?: string) => {
    if (!slotId) return null;
    // Extract first 16 characters: "DD.MM.YYYY-HH:MM"
    return slotId.substring(0, 16);
};
